<template>
  <div class="project" >
    
    <!-- Navbar -->
    <nav class="navbar is-mobile is-medium" style="background-color: #30008A;" role="navigation" aria-label="main navigation">
      <div class="navbar-brand">
          <img id="navbar-logo" src="../../assets/projects/smartcitylights/glow20.jpg" alt="Solid Logo">
      </div>
    </nav>
    
    <!-- Project Summary -->
    <section>
      <h1>GLOW 2020 "Smart City Light" Project Summary</h1>
      <p>
        In the midst of the challenges posed by the COVID-19 pandemic, the GLOW 2020 project illuminated the path to innovation and adaptation. Collaborating with Eindhoven light artist Philip Ross, we introduced an innovative concept for street lighting. Our vision prioritized energy-efficiency, vibrant multicolored lighting, and a focus on eco-friendliness and personalization.
      </p>
    </section>

    <!-- My Role in the Project -->
    <section>
      <h2>My Role in the Project</h2>
      <p>
        My involvement included crafting modules and preparing Xiaomi LED lights for transformation. I also played a pivotal role in setting up and demonstrating these innovative lights in various Eindhoven parks, evaluating their suitability for diverse neighborhoods and inhabitants, human and animal alike.
      </p>
    </section>
    <br>
    <div class="photo-grid">
    <div class="columns is-multiline">
      <div class="column is-half">
        <figure class="image">
          <video width="100%" src='../../assets/projects/smartcitylights/vid1.mp4' controlsList="nodownload" controls disablePictureInPicture />
        </figure>
      </div>
      <div class="column is-half">
        <figure class="image">
          <video src="../../assets/projects/smartcitylights/vid2.mp4" muted loop autoplay ></video>
        </figure>
      </div>
    </div>
    <!-- Technical Aspects - Lamp Hardware -->
    <section>
      <h2>Technical Aspects</h2>
      <p>
        <strong>Lamp Hardware:</strong> The lamp hardware was controlled via a custom-made printed circuit board, integrating the controller, Wi-Fi module, and LED driver. (Image: Image showing lamp hardware and components)
      </p>
      <!-- Sample code for lamp hardware -->
      <div v-highlight>
        <pre class="language-javascript">
          <code>
            # Sample code to control lamp hardware
            print("Setting lamp red value to 255")
            arr[0] = (lamps[0].red >> 8) & 0xFF
            arr[1] = lamps[0].red & 0xFF
          </code>
        </pre>
      </div>
    </section>
    <br>

    <!-- Technical Aspects - Communication Protocol -->
    <section>
      <p>
        <strong>Communication Protocol:</strong> We developed a communication protocol for handling dimming and RGB values, using 16 bits (2 bytes) to represent 65,536 possible steps. Each lamp's values were sent in a specific order within a byte array.
      </p>
      <!-- Sample code for communication protocol -->
      <div v-highlight>
        <pre class="language-javascript">
          <code>
            # Sample code to create a byte array for lamp communication
            for i in range(410):
                # Code for populating the byte array
                # ...
            </code>
        </pre>
      </div>
    </section>
    <br>

    <!-- Technical Aspects - C4 Model -->
    <section>
      <p>
        <strong>C4 Model:</strong> A C4 model visualized the system's architecture and data flow, providing clarity about the components and their interactions. (Image: Diagram of the C4 model)
      </p>
    </section>
    <br>
    <div class="columns is-multiline is-mobile">
        <!-- C1: Overview of smartcitylights -->
        <div class="column is-half">
          <img src="../../assets/projects/smartcitylights/c1.png" alt="C1: Overview of smartcitylights" />
        </div>

        <!-- C2: A Deeper Look into smartcitylights -->
        <div class="column is-half">
          <img src="../../assets/projects/smartcitylights/c2.png" alt="C2: A Deeper Look into smartcitylights"/>
        </div>

        <!-- C3: The Project Add and Wizard Components -->
        <div class="column is-half">
          <img src="../../assets/projects/smartcitylights/c3.png" alt="C3: The Project Add and Wizard Components" />
        </div>

        <!-- C4: UML Data Flow within the Wizard -->
        <div class="column is-half">
          <img src="../../assets/projects/smartcitylights/c4.png" alt="C4: UML Data Flow within the Wizard" />
        </div>
      </div>

    <br>
    <!-- Technical Aspects - Python ByteArrays -->
    <section>
      <p>
        <strong>Python ByteArrays:</strong> Python byte arrays were crucial for creating and manipulating lamp values, allowing precise control and customization.
      </p>
      <!-- Sample code for Python ByteArrays -->
      <div v-highlight>
        <pre class="language-javascript">
          <code>
            # Sample code to create and manipulate a byte array
            array = []
            # Code for creating the byte array
            # ...
          </code>
        </pre>
      </div>
    </section>
    <br>

    <!-- Technical Aspects - UDP Communication -->
    <section>
      <p>
        <strong>UDP Communication:</strong> We communicated with the lamps via UDP over a socket, with a limitation - we couldn't confirm message receipt due to the lamps being UDP-only devices.
      </p>
      <!-- Sample code for UDP communication -->
      <div v-highlight>
        <pre class="language-javascript">
          <code>
            # Sample code for sending data over UDP
            HOST = '192.168.4.204'  # Lamp IP
            PORT = 4210  # Port of the philip's lamp
            # Code for sending data over UDP
            # ...
          </code>
        </pre>
      </div>
    </section>

    <!-- Technical Aspects - Web Interface -->
    <section>
      <p>
        <strong>Web Interface:</strong> We repurposed an existing web page to control and update individual LED lamps. Each adjustment triggered a new byte array sent to the lamp. (Image: Screenshot of the web interface)
      </p>
    </section>

    <!-- Technical Aspects - Animations -->
    <section>
      <p>
        <strong>Animations:</strong> We explored lamp animations by defining states and sending corresponding byte arrays, allowing for dynamic and captivating lighting effects.
      </p>
      <!-- Sample code for managing lamp animations -->
      <div v-highlight>
        <pre class="language-javascript">
          <code>
            interval = 0.5
            stateOne = [] #long array inside
            stateTwo = [] #long array inside
            stateThree = [] #long array inside
            stateFour = [] #long array inside
            stateFive = [] #long array inside
            stateOff = [] #long array inside
            states = [stateOne, stateTwo, stateThree, stateFour, stateFive, stateOff]

            def sendAnimation(message):
                HOST = '192.168.4.204'  # Lamp IP
                PORT = 4210 # Port of the philip's lamp

                print("UDP target IP: %s" % HOST)
                print("UDP target port: %s" % PORT)

                try:
                    sock = socket.socket(socket.AF_INET, socket.SOCK_DGRAM) # Internet, UDP
                    sock.sendto(bytearray(message), (HOST, PORT))
                except:
                    print("Unexpected error:", sys.exc_info()[0])

                print("Sent")

            currentstate = 0

            <!-- while (currentstate < 7): -->
                if currentstate == 0: #State 1
                    sendAnimation(states[0])

                elif currentstate == 1: #State 2
                    sendAnimation(states[1])

                elif currentstate == 2: #State 3
                    sendAnimation(stateTwo)

                elif currentstate == 3: #State 4
                    sendAnimation(states[3])

                elif currentstate == 4: #State 5
                    sendAnimation(states[4])

                elif currentstate == 5: #State 5
                    sendAnimation(states[5])
                    currentstate = -1    

                time.sleep(interval) # Sleep for 'X' amount of seconds
                currentstate = currentstate + 1
          </code>
        </pre>
      </div>
    </section>

    <!-- Conclusion -->
    <section>
      <h2>Conclusion</h2>
      <p>
        In conclusion, the "Smart City Light" project of GLOW 2020 demonstrated our ability to adapt and innovate in challenging circumstances. Although constrained by the lamp's limited hardware, we succeeded in creating a dynamic, eco-friendly, and customizable smart lighting system. The project showcased our creative problem-solving and technical expertise.
      </p>
      <p>
        This project not only illuminated the streets of Eindhoven but also our journey in exploring innovative solutions for a smarter, more sustainable future.
      </p>
    </section>
     <br>
    <div class="photo-grid">
    <div class="columns is-multiline">
      
      <div class="column is-half">
        <figure class="image">
          <img src="../../assets/projects/smartcitylights/lamp.jpg" alt="Image 1">
        </figure>
      </div>
      <div class="column is-half">
        <figure class="image">
          <img src="../../assets/projects/smartcitylights/image5.gif" alt="Image 3">
        </figure>
      </div>
      <div class="column is-one-third">
        <figure class="image">
          <img src="../../assets/projects/smartcitylights/img1.jpg" alt="Image 4">
        </figure>
      </div>
      <div class="column is-one-third">
        <figure class="image">
          <img src="../../assets/projects/smartcitylights/img2.jpg" alt="Image 5">
        </figure>
      </div>
      <div class="column is-one-third">
        <figure class="image">
          <img src="../../assets/projects/smartcitylights/img3.jpg" alt="Image 6">
        </figure>
        </div>
        <div class="column is-one-third">
        <figure class="image">
          <img src="../../assets/projects/smartcitylights/img4.jpg" alt="Image 6">
        </figure>
      </div>
      <div class="column is-one-third">
        <figure class="image">
          <img src="../../assets/projects/smartcitylights/img5.jpg" alt="Image 6">
        </figure>
      </div>
      <div class="column is-one-third">
        <figure class="image">
          <img src="../../assets/projects/smartcitylights/storage.jpg" alt="Image 6">
        </figure>
      </div>
  </div></div></div>
  <br><br>


  </div>
</template>

<style scoped>
  @import 'project.css';
</style>
